import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getHydrateTimetableAction, getSetCourseSectionsAction } from '../actions/course';
import DataContext from '../context';
import { enforceMatchingSelections, getMatchingPairs, sectionMatchesPrefix } from '../utils/matchingUtils';
import { loadTimetable, storeTimetable } from '../utils/plannerStorage';
import {
    getGridBounds,
    getSectionObjs,
    getVirtualSectionBoxes,
    meetingsOverlap,
    parseSectionToCourseBoxes,
} from '../utils/timetableActions';
import getSections from '../timetable/getSections';

function getSectionType(section) {
    return section.match(/^[A-Z]+/i)?.[0] || '';
}

function getCourseBoxes(data, timetable) {
    const regular = [];
    const tba = [];

    Object.entries(timetable).forEach(([courseCode, selectedSections]) => {
        const course = data[courseCode];
        if (!course) return;

        const courseSections = getSections(data, courseCode);
        selectedSections.forEach(section => {
            const sectionType = getSectionType(section);
            const isSingleton = courseSections?.[sectionType]?.length === 1;
            getSectionObjs(data, courseCode, section).forEach(sectionObj => {
                parseSectionToCourseBoxes(courseCode, section, sectionObj, isSingleton, false).forEach(box => {
                    if (box.type === 'tba') {
                        tba.push(box);
                    } else {
                        regular.push(box);
                    }
                });
            });
        });
    });

    return { regular, tba };
}

function markConflicts(boxes) {
    return boxes.map((box, index) => ({
        ...box,
        hasConflict: boxes.some((candidate, candidateIndex) => (
            index !== candidateIndex
            && meetingsOverlap(box, candidate)
        )),
    }));
}

function getUpdatedSelections(course, currentSections, section, toggle) {
    const sectionType = getSectionType(section);
    const available = getSections({ [course.code]: course }, course.code)?.[sectionType] || [];
    const isSingleton = available.length === 1;
    const isSelected = currentSections.includes(section);

    if (toggle && isSelected) {
        if (isSingleton) return currentSections;
        const isLecture = /^L\d/i.test(section);
        const pairs = getMatchingPairs(course);
        const hasExplicitPairs = pairs.length > 0;

        if (hasExplicitPairs) {
            if (isLecture) {
                const pairedSections = pairs
                    .filter(p => sectionMatchesPrefix(section, p.lecture))
                    .flatMap(p => [p.tutorial]);
                return currentSections.filter(c => (
                    c !== section && !pairedSections.some(t => sectionMatchesPrefix(c, t))
                ));
            } else {
                const matchedPair = pairs.find(p => sectionMatchesPrefix(section, p.tutorial));
                if (matchedPair) {
                    return currentSections.filter(c => (
                        c !== section && !sectionMatchesPrefix(c, matchedPair.lecture)
                    ));
                }
            }
        }

        return currentSections.filter(c => c !== section);
    }

    const withoutSameType = currentSections.filter(candidate => getSectionType(candidate) !== sectionType);
    return enforceMatchingSelections(course, [...withoutSameType, section], section);
}

export function useReactTimetable() {
    const { data, selectedTerm } = useContext(DataContext);
    const dispatch = useDispatch();
    const timetable = useSelector(state => state.app.timetable);
    const [virtualBoxes, setVirtualBoxes] = useState([]);
    const [hydratedTerm, setHydratedTerm] = useState(null);

    useEffect(() => {
        if (!data || !selectedTerm || hydratedTerm === selectedTerm) return;

        dispatch(getHydrateTimetableAction(loadTimetable(selectedTerm, data)));
        setHydratedTerm(selectedTerm);
    }, [data, dispatch, hydratedTerm, selectedTerm]);

    useEffect(() => {
        if (!selectedTerm || hydratedTerm !== selectedTerm) return;
        storeTimetable(selectedTerm, timetable);
    }, [hydratedTerm, selectedTerm, timetable]);

    const { courseBoxes, tbaBoxes } = useMemo(() => {
        const boxes = data ? getCourseBoxes(data, timetable) : { regular: [], tba: [] };
        return {
            courseBoxes: markConflicts(boxes.regular),
            tbaBoxes: boxes.tba,
        };
    }, [data, timetable]);

    const showVirtualCourses = useCallback((code, section) => {
        if (!data) return;
        setVirtualBoxes(getVirtualSectionBoxes(
            data,
            code,
            section,
            getSections(data, code),
            timetable[code]
        ));
    }, [data, timetable]);

    const hideVirtualCourses = useCallback(() => setVirtualBoxes([]), []);

    const setSelection = useCallback((code, section, toggle = false) => {
        const course = data?.[code];
        if (!course || !timetable[code]) return;

        const sections = getUpdatedSelections(course, timetable[code], section, toggle);
        dispatch(getSetCourseSectionsAction(code, sections));
    }, [data, dispatch, timetable]);

    const addSection = useCallback((code, section) => setSelection(code, section), [setSelection]);
    const removeSection = useCallback((code, section) => setSelection(code, section, true), [setSelection]);
    const swapSection = useCallback((code, oldSection, newSection) => {
        if (oldSection !== newSection) setSelection(code, newSection);
    }, [setSelection]);

    const allRegularBoxes = useMemo(
        () => [...courseBoxes, ...virtualBoxes.filter(box => box.type === 'regular')],
        [courseBoxes, virtualBoxes]
    );

    return {
        courseBoxes,
        tbaBoxes,
        virtualBoxes,
        allRegularBoxes,
        gridBounds: getGridBounds(allRegularBoxes),
        showVirtualCourses,
        hideVirtualCourses,
        addSection,
        removeSection,
        swapSection,
    };
}

export default useReactTimetable;
